"use client";

import React from "react";
import "./OurServices.scss";
import { ReactSVG } from "react-svg";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { useMediaQuery } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import {
  slideFromLeft,
  slideFromRight,
  fadeUp,
  defaultViewport,
} from "@/utils/animations";
import { strapiImageUnoptimized } from "@/lib/strapi/strapiImage";

import OurServicesBannersSkeleton from "./OurServicesBannersSkeleton";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export type ServiceBannersPayload = {
  fourth: { desktop: string; mobile: string };
  fifth: { desktop: string; mobile: string };
  sixth: { desktop: string; mobile: string };
};

type OurServicesClientProps = {
  serviceBanners?: ServiceBannersPayload | null;
};

export default function OurServicesClient({ serviceBanners }: OurServicesClientProps) {
  const router = useRouter();
  const isMobile = useMediaQuery("(max-width:1023px)");

  return (
    <div className="home-our-services-wrapper baseContainer">
      <div className="maxWidth w-full">
        <div className="first-section">
          <motion.div
            className="left"
            variants={slideFromLeft}
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
          >
            <p className="section-main-header black">Our Services</p>
            <p className="text-medium-24 weight-500 grey-700">
              At Amherst Supply, we are dedicated to providing comprehensive services to support your
              projects from start to finish.{" "}
            </p>

            {!isMobile && (
              <button className="main-btn red" onClick={() => router.push("/services")}>
                Learn More
              </button>
            )}
          </motion.div>

          <motion.div
            className="right"
            variants={slideFromRight}
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
          >
            <div className="column">
              <div className="row">
                <ReactSVG src="/assets/Home/OurServices/icon-1.svg" />
                <p className="text-medium-24 weight-600 black">3D Designs</p>
              </div>

              <div className="row">
                <ReactSVG src="/assets/Home/OurServices/icon-2.svg" />
                <p className="text-medium-24 weight-600 black">Ontario&apos;s Lottery & Gaming</p>
              </div>
            </div>

            <div className="column">
              <div className="row">
                <ReactSVG src="/assets/Home/OurServices/icon-3.svg" />
                <p className="text-medium-24 weight-600 black">Paint Mixing & Colour Matching</p>
              </div>

              <div className="row">
                <ReactSVG src="/assets/Home/OurServices/icon-4.svg" />
                <p className="text-medium-24 weight-600 black">Key Cutting</p>
              </div>
            </div>
          </motion.div>

          {isMobile && (
            <button className="main-btn red" onClick={() => router.push("/services")}>
              Learn More
            </button>
          )}
        </div>

        <div className="second-section">
          <motion.div
            className="left"
            variants={slideFromLeft}
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
          >
            <p className="section-main-header large black">
              Order Your Chickens at{" "}
              <span className="section-main-header large white bg-blue">Amherst Supply!</span>
            </p>

            <p className="text-small-20 grey-700">
              Explore our variety of healthy breeds available for pickup. Start your poultry journey
              with us today!
            </p>

            {!isMobile && (
              <button onClick={() => router.push("/chickens")} className="main-btn blue">
                Explore Options{" "}
              </button>
            )}
          </motion.div>

          <motion.div
            className="right"
            variants={slideFromRight}
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
          >
            <img src="/assets/Home/OurServices/services-chicken.webp" alt="services-chicken" />
          </motion.div>

          {isMobile && (
            <button onClick={() => router.push("/chickens")} className="main-btn blue">
              Explore Options{" "}
            </button>
          )}
        </div>

        {!isMobile && !serviceBanners && <OurServicesBannersSkeleton />}

        {!isMobile && serviceBanners && (
          <motion.div
            className="third-section"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
          >
            <div className="left">
              <Image
                src={serviceBanners.fourth.desktop}
                alt="Amherst Supply services"
                fill
                loading="lazy"
                unoptimized={strapiImageUnoptimized(serviceBanners.fourth.desktop)}
              />
            </div>

            <div className="right">
              <div className="top">
                <Image
                  src={serviceBanners.fifth.desktop}
                  alt="Amherst Supply services"
                  fill
                  loading="lazy"
                  unoptimized={strapiImageUnoptimized(serviceBanners.fifth.desktop)}
                />
              </div>

              <div className="bottom">
                <Image
                  src={serviceBanners.sixth.desktop}
                  alt="Amherst Supply services"
                  fill
                  loading="lazy"
                  unoptimized={strapiImageUnoptimized(serviceBanners.sixth.desktop)}
                />
              </div>
            </div>
          </motion.div>
        )}

        {isMobile && serviceBanners && (
          <>
            <Swiper
              navigation={{
                nextEl: ".our-services-next-arrow",
                prevEl: ".our-services-prev-arrow",
              }}
              pagination={{
                clickable: true,
                el: ".swiper-pagination-dots-services",
                type: "bullets",
              }}
              modules={[Navigation, Pagination]}
              slidesPerView={isMobile ? 1.2 : 3}
              spaceBetween={16}
            >
              <SwiperSlide className="slide">
                <div className="slide-service">
                  <Image
                    src={serviceBanners.fourth.mobile}
                    alt="Amherst Supply services"
                    fill
                    loading="lazy"
                    unoptimized={strapiImageUnoptimized(serviceBanners.fourth.mobile)}
                  />
                </div>
              </SwiperSlide>

              <SwiperSlide className="slide">
                <div className="slide-service">
                  <Image
                    src={serviceBanners.fifth.mobile}
                    alt="Amherst Supply services"
                    fill
                    loading="lazy"
                    unoptimized={strapiImageUnoptimized(serviceBanners.fifth.mobile)}
                  />
                </div>
              </SwiperSlide>

              <SwiperSlide className="slide">
                <div className="slide-service">
                  <Image
                    src={serviceBanners.sixth.mobile}
                    alt="Amherst Supply services"
                    fill
                    loading="lazy"
                    unoptimized={strapiImageUnoptimized(serviceBanners.sixth.mobile)}
                  />
                </div>
              </SwiperSlide>
            </Swiper>
            <div className="swiper-pagination-dots-services" />
          </>
        )}

        <div className="fourth-section">
          <motion.div
            className="left"
            variants={slideFromLeft}
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
          >
            <p className="section-main-header black">Flexiti Financing</p>
            <p className="text-medium-24 grey-600 weight-500">
              Flexiti Financing offers flexible financing options with affordable monthly payments,
              quick application processes, and high approval rates. Ideal for managing both large
              renovations and smaller projects.
            </p>

            {!isMobile && (
              <button className="main-btn white-pink" onClick={() => router.push("/flexiti-financing")}>
                Apply Now
              </button>
            )}
          </motion.div>

          <motion.div
            className="right"
            variants={slideFromRight}
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
          >
            <img
              src={
                isMobile
                  ? "/assets/Home/OurServices/flexiti-mobile.svg"
                  : "/assets/Home/OurServices/flexiti.svg"
              }
              alt="amherst supply"
            />
          </motion.div>

          {isMobile && (
            <button className="main-btn white-pink" onClick={() => router.push("/flexiti-financing")}>
              Apply Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
